import { SavedApplicationResponse } from 'src/features/common/types/salesforce/api/otp/saved-application-response';
import { request } from '../../../services/request';
import { ICacheStore } from '../context/cache-context';
import { getProductApiFromDTO } from '../helpers/dto-mappers/salesforce/get-product-api-from-dto';
import { getPaymentMethodAPIFromDTO } from '../helpers/dto-mappers/salesforce/get-payment-method-api-from-dto';
import { ApplicationAPI } from '../types/salesforce/api/application/application-api';
import { SalesforceApplicationSession } from '../types/salesforce/misc/salesforce-application-session';
import { getDtoFromApplicationApi } from '../helpers/dto-mappers/salesforce/get-dto-from-application-api';
import { ApplicationDTO } from '../types/salesforce/dto/application/application-dto';
import {
  RequestOtpResponse,
  RequestOtpResponseType,
} from '../types/salesforce/api/otp/request-otp-response';
import { getApplicationAPIFromDTO } from '../helpers/dto-mappers/salesforce/get-application-api-from-dto';
import {
  GetApplicationResponse,
  GetApplicationResponseType,
} from '../types/salesforce/api/otp/get-application-response';
import { DocumentAPI } from '../types/salesforce/api/document/document-api';
import { DocumentDTO } from '../types/salesforce/dto/application/document-dto';
import { getDocumentDTOFromDocumentAPI } from '../helpers/dto-mappers/salesforce/get-document-dto-from-document-api';
import { DeleteDocumentDTO } from '../types/salesforce/dto/document/delete-document-dto';
import { ApplicationDTOWithDocuments } from '../types/salesforce/dto/otp/application-dto-with-documents';
import { getDocumentApiFromDocumentDTO } from '../helpers/dto-mappers/salesforce/get-document-api-from-document-dto';
import { StepNames } from '../helpers/dto-mappers/salesforce/step-names';
import { PendingAppService } from './pending-apps-service';
import { ResponseCodeSF } from '../types/salesforce/dto/application/category-dto';
import { LeadDTO } from '../types/salesforce/dto/application/lead-dto';
import { Api } from 'src/common';
import {
  FICA_STATUS,
  SalesforceResponse,
} from 'src/features/common/types/salesforce/api/application/salesforce-response';
import { TerminalDetailsAPI } from '../types/salesforce/api/terminal-details/terminal-details-api';
import { OnboardingDocumentTypeEnum } from 'src/features/onboarding/types/upload-documents';
import { MerchantAgreement } from 'src/features/common/types/application/merchant-agreement';
import { OpportunityDTO, SolutionType } from 'src/features/common/types';

export const VALIDATION_QUINTICA_FILE = 'Validation Certificate';

export interface ISalesforceService {
  refreshFicaStatus(
    refNumber: string,
    abortController?: AbortController,
  ): Promise<FICA_STATUS | null>;
  getProducts(abortController?: AbortController): Promise<TerminalDetailsAPI>;
  createLead(abortController?: AbortController): Promise<SavedApplicationResponse | undefined>;
  saveApplication(
    application: ApplicationAPI,
    isLightVetting?: boolean,
    abortController?: AbortController,
  ): Promise<SavedApplicationResponse | undefined>;
  requestOTP(
    emailAddress: string,
    referenceNumber: string,
    abortController?: AbortController,
  ): Promise<RequestOtpResponse>;
  getApplication(
    emailAddress: string,
    referenceNumber: string,
    otp: string,
    abortController?: AbortController,
  ): Promise<GetApplicationResponse>;
  saveDocuments(
    documents: DocumentAPI[],
    solutionType: SolutionType,
    abortController?: AbortController,
    pendingAppFieldNames?: string[],
  ): Promise<void>;
  saveMerchantAgreement(
    merchant: MerchantAgreement,
    solutionType: SolutionType,
    abortController?: AbortController,
  ): Promise<void>;
}

export class SalesforceService implements ISalesforceService {
  cache: ICacheStore;
  productsPromise?: Promise<TerminalDetailsAPI>;

  lead?: LeadDTO;

  // used for document uploads to exclude the selfie documents from deletion logic
  SELFIE_DOCUMENT_TYPE = 'Selfie';
  TOKEN_CACHE_KEY = 'token_session';
  SALESFORCE_TOKEN_SESSION = 'salesforce_token_session';
  // constants - URLS
  TOKEN_ENDPOINT_PATH = 'salesforce/token';
  APPLICATION_ORDER_JOURNEY_ENDPOINT_PATH = 'services/apexrest/LightVetting';
  APPLICATION_ONBOARDING_JOURNEY_ENDPOINT_PATH = 'services/apexrest/Merchant';
  APPLICATION_ONBOARDING_LEAD_PATH = 'services/apexrest/OnboardingLead';
  TERMINALS_ENDPOINT_PATH = 'services/apexrest/Terminals';
  OTP_ENDPOINT_PATH = 'services/apexrest/OTP';
  DOCUMENTS_ENDPOINT_PATH = 'services/apexrest/OnboardingDocuments';
  RETRIEVE_APPLICATION_PATH = 'services/apexrest/retrieveApplicationData';

  // constants - cache keys - self onboarding
  SALESFORCE_APPLICATION_SESSION = 'salesforce application session';
  SALESFORCE_APPLICATION_SESSION_DEVICE_ACTIVATION =
    'salesforce application session device activation';
  SALESFORCE_APPLICATION_SESSION_3PO = 'salesforce application session 3PO';

  constructor(cache: ICacheStore) {
    this.cache = cache;
  }

  async createLead(
    abortController?: AbortController,
  ): Promise<SavedApplicationResponse | undefined> {
    const step = 'Monthly Card Turnover';
    const saveEndpointURL = this.APPLICATION_ONBOARDING_LEAD_PATH;

    const session = await this.getSession(abortController);

    const replacer = (key, value) => (value === undefined ? null : value);
    const response = await request(
      saveEndpointURL,
      {
        fetchOptions: {
          method: 'POST',
          body: JSON.stringify(
            {
              ...this.lead,
              step_name: step,
            },
            replacer,
          ),
          headers: { Authorization: `Bearer ${session.accessToken}` },
          signal: abortController?.signal || null,
        },
      },
      session.instanceUrl,
      null,
      false,
    );
    const responseCode = response?.status;
    const responseBody = response?.result;
    if (responseCode === 200 && responseBody?.ResponseData) {
      const applicationResponseDTO: ApplicationDTO = JSON.parse(responseBody.ResponseData);

      this.cache.set(this.SALESFORCE_APPLICATION_SESSION, {
        opportunityId: applicationResponseDTO.opportunity?.id,
        accountId: applicationResponseDTO.account?.id,
        contactId: applicationResponseDTO.contact?.id,
      } as SalesforceApplicationSession);

      return {
        referenceNumber: applicationResponseDTO.opportunity?.reference_no,
      };
    } else if (!abortController?.signal?.aborted) {
      throw new Error(
        `SF revamp: error response from salesforce ${saveEndpointURL} POST endpoint: response code: ${responseCode}, response: ${responseBody?.Message}`,
      );
    }
  }

  getSessionKey(application?: ApplicationAPI, opportunity?: OpportunityDTO): string {
    if (
      (application && !!application.brokerLogin) ||
      (application && application.solutionType === SolutionType.thirdPartyOnboarding) ||
      (opportunity && opportunity.solutionType === SolutionType.thirdPartyOnboarding)
    ) {
      return this.SALESFORCE_APPLICATION_SESSION_3PO;
    }

    if (
      (application && application.solutionType === SolutionType.buyInStoreFicaOnline) ||
      (opportunity && opportunity.solutionType === SolutionType.buyInStoreFicaOnline)
    ) {
      return this.SALESFORCE_APPLICATION_SESSION_DEVICE_ACTIVATION;
    }

    return this.SALESFORCE_APPLICATION_SESSION;
  }

  async saveApplication(
    application: ApplicationAPI,
    isLightVetting?: boolean,
    abortController?: AbortController,
  ): Promise<SavedApplicationResponse | undefined> {
    const saveEndpointURL = isLightVetting
      ? this.APPLICATION_ORDER_JOURNEY_ENDPOINT_PATH
      : this.APPLICATION_ONBOARDING_JOURNEY_ENDPOINT_PATH;
    const sessionKey = this.getSessionKey(application);
    const session = await this.getSession(abortController);
    const applicationSession: SalesforceApplicationSession = this.cache.get(sessionKey);

    const applicationDTO = getDtoFromApplicationApi(application, applicationSession);
    // convert undefined values to nulls before sending to Salesforce
    const replacer = (key, value) => (value === undefined ? null : value);
    const response = await request(
      saveEndpointURL,
      {
        fetchOptions: {
          method: 'POST',
          body: JSON.stringify(applicationDTO, replacer),
          headers: { Authorization: `Bearer ${session.accessToken}` },
          signal: abortController?.signal || null,
        },
      },
      session.instanceUrl,
      null,
      false,
    );
    const responseCode = response?.status;
    const responseBody = response?.result;
    if (responseCode === 200 && responseBody?.ResponseData) {
      const applicationResponseDTO: ApplicationDTO = JSON.parse(responseBody.ResponseData);

      if (!applicationSession) {
        this.cache.set(sessionKey, {
          opportunityId: applicationResponseDTO.opportunity?.id,
          accountId: applicationResponseDTO.account?.id,
          contactId: applicationResponseDTO.contact?.id,
        } as SalesforceApplicationSession);
      }

      return {
        referenceNumber: applicationResponseDTO.opportunity?.reference_no,
      };
    } else if (!abortController?.signal?.aborted) {
      throw new Error(
        `SF revamp: error response from salesforce ${saveEndpointURL} POST endpoint: response code: ${responseCode}, response: ${responseBody?.Message}`,
      );
    }
  }

  getProducts(abortController?: AbortController): Promise<TerminalDetailsAPI> {
    if (this.productsPromise) {
      return this.productsPromise;
    }

    const products = this.cache.get(this.TERMINALS_ENDPOINT_PATH);
    if (products) {
      this.productsPromise = Promise.resolve(products);
      return this.productsPromise;
    }

    this.productsPromise = this.getSession(abortController).then(
      (session): Promise<TerminalDetailsAPI> => {
        if (!session.instanceUrl)
          return new Promise(() => ({
            products: [],
            paymentMethods: [],
          }));

        return request(
          this.TERMINALS_ENDPOINT_PATH,
          {
            fetchOptions: {
              method: 'GET',
              headers: { Authorization: `Bearer ${session.accessToken}` },
            },
          },
          session.instanceUrl,
          null,
          false,
        ).then((response): TerminalDetailsAPI => {
          const responseCode = response?.status;
          const responseBody = response?.result;
          if (responseCode === 200 && responseBody?.ResponseData) {
            const terminals = JSON.parse(responseBody.ResponseData)?.terminals || [];
            const paymentMethods = JSON.parse(responseBody.ResponseData)?.paymentMethods || [];
            const terminalsResult = terminals.map((terminal) => getProductApiFromDTO(terminal));
            const paymentMethodResult = paymentMethods.map((method) =>
              getPaymentMethodAPIFromDTO(method),
            );
            const terminalDetails: TerminalDetailsAPI = {
              products: terminalsResult,
              paymentMethods: paymentMethodResult,
            };
            this.cache.set(this.TERMINALS_ENDPOINT_PATH, terminalDetails);
            return terminalDetails;
          } else if (!abortController?.signal?.aborted) {
            throw new Error(
              `Terminals revamp: error response from salesforce ${this.TERMINALS_ENDPOINT_PATH} GET endpoint: response code: ${responseCode}`,
            );
          }
          throw new Error('Logic error, this will never be reached.');
        });
      },
    );
    return this.productsPromise;
  }

  private sessionExpired(): boolean {
    const tokenSession = sessionStorage.getItem(this.TOKEN_CACHE_KEY);

    if (!tokenSession) return true;

    const parsedSessionTime = parseInt(tokenSession, 10);
    const now = Date.now();

    // 30 minutes session
    return !!(parsedSessionTime && now - parsedSessionTime >= 30 * 60 * 1000);
  }

  private getSession(abortController?: AbortController): Promise<SalesforceSession> {
    const salesforceSessionToken = sessionStorage.getItem(this.SALESFORCE_TOKEN_SESSION);

    if (!this.sessionExpired() && salesforceSessionToken) {
      const token: SalesforceSession = JSON.parse(salesforceSessionToken);

      return Promise.resolve(token);
    }

    return request(
      this.TOKEN_ENDPOINT_PATH,
      {
        fetchOptions: {
          method: 'GET',
          signal: abortController?.signal || null,
        },
      },
      null,
      null,
      false,
    ).then((response) => {
      const responseCode = response?.status;
      const responseBody = response?.result;
      if (
        !abortController?.signal?.aborted &&
        (responseCode !== 200 || !responseBody?.access_token || !responseBody?.instance_url)
      ) {
        throw new Error(
          `Token revamp: error response from vfs-token-service ${this.TOKEN_ENDPOINT_PATH} POST endpoint: response code: ${responseCode}`,
        );
      }

      const result = {
        accessToken: responseBody?.access_token,
        instanceUrl: responseBody?.instance_url,
      } as SalesforceSession;

      sessionStorage.setItem(this.TOKEN_CACHE_KEY, Date.now().toString());
      sessionStorage.setItem(this.SALESFORCE_TOKEN_SESSION, JSON.stringify(result));

      return result;
    });
  }

  refreshFicaStatus(
    refNumber: string,
    abortController?: AbortController,
  ): Promise<FICA_STATUS | null> {
    return this.getSession().then(async (session) => {
      const { response, status } = await Api.get<SalesforceResponse>(
        `${session.instanceUrl}/${this.RETRIEVE_APPLICATION_PATH}?refNumber=${refNumber}`,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.accessToken}`,
          },
          signal: abortController?.signal || null,
        },
      );

      if (status !== 200) {
        throw new Error(
          `OTP revamp: error response from salesforce ${this.RETRIEVE_APPLICATION_PATH} POST endpoint: response code: ${status}`,
        );
      }

      const reviver = (key, value) => (value === null ? undefined : value);
      const responseData = JSON.parse(response.ResponseData, reviver);

      return responseData?.merchant?.opportunity?.fica_status || null;
    });
  }

  requestOTP(
    emailAddress: string,
    referenceNumber: string,
    abortController?: AbortController,
  ): Promise<RequestOtpResponse> {
    return this.getSession(abortController).then(async (session) => {
      const { response, status } = await Api.post<SalesforceResponse>(
        `${session.instanceUrl}/${this.OTP_ENDPOINT_PATH}?refNumber=${referenceNumber}&emailAddress=${emailAddress}`,
        {
          headers: { Authorization: `Bearer ${session.accessToken}` },
          signal: abortController?.signal || null,
        },
      );

      if (status !== 200 && !abortController?.signal?.aborted) {
        throw new Error(
          `OTP revamp: error response from salesforce ${this.OTP_ENDPOINT_PATH} POST endpoint: response code: ${status}`,
        );
      }

      const error = response?.Message;
      switch (error) {
        case 'OTP created.':
          return { type: RequestOtpResponseType.SUCCESS_OTP_SUBMITTED };
        case 'Record not exist.':
          return { type: RequestOtpResponseType.ERROR_REFERENCE_NUMBER_INCORRECT };
        case 'Application is already finished':
          return { type: RequestOtpResponseType.ERROR_APPLICATION_SUBMITTED };
        default:
          throw new Error(
            `OTP revamp: error response from salesforce ${this.OTP_ENDPOINT_PATH} POST endpoint: response error: ${error}`,
          );
      }
    });
  }

  getApplication(
    emailAddress: string,
    referenceNumber: string,
    otp: string,
    abortController?: AbortController,
  ): Promise<GetApplicationResponse> {
    return this.getSession(abortController).then(async (session) => {
      const { response, status } = await Api.get<SalesforceResponse>(
        `${session.instanceUrl}/${this.OTP_ENDPOINT_PATH}?refNumber=${referenceNumber}&emailAddress=${emailAddress}&otpCode=${otp}`,
        {
          headers: { Authorization: `Bearer ${session.accessToken}` },
          signal: abortController?.signal || null,
        },
      );

      if (status !== 200 && !response?.ResponseData && !abortController?.signal?.aborted) {
        throw new Error(
          `OTP revamp: error response from salesforce ${this.OTP_ENDPOINT_PATH} POST endpoint: response code: ${status}`,
        );
      }

      const message = response?.Message;

      switch (message) {
        case 'OTP code is incorrect.':
          return {
            error: GetApplicationResponseType.ERROR_OTP_CODE_INCORRECT,
          } as GetApplicationResponse;
        case 'OTP code is to old.':
          return {
            error: GetApplicationResponseType.ERROR_OTP_CODE_TOO_OLD,
          } as GetApplicationResponse;
        case null:
          // convert null values from Salesforce to undefined
          const reviver = (key, value) => (value === null ? undefined : value);
          const data = JSON.parse(response.ResponseData, reviver);

          const applicationResponseDTO: ApplicationDTOWithDocuments =
            data.lightVetting || data.merchant || {};

          if (data.lead as LeadDTO) {
            applicationResponseDTO.lead = {
              ...data.lead,
              locked_fields: data.locked_fields,
            };
            this.lead = data.lead;
          }

          const sessionKey = this.getSessionKey(undefined, applicationResponseDTO.opportunity);
          const sfApplicationSession: SalesforceApplicationSession = {
            opportunityId: applicationResponseDTO.opportunity?.id,
            accountId: applicationResponseDTO.account?.id,
            contactId: applicationResponseDTO.contact?.id,
          };
          this.cache.set(sessionKey, sfApplicationSession);

          // CPA
          if (data.responses && data.responses?.length) {
            applicationResponseDTO.categories = await PendingAppService.getUpdateInformation(
              {
                order_ref_number: applicationResponseDTO.opportunity?.reference_no || '',
                journey_name: 'pos',
                responses: (data.responses || []).map((i) => i.response) as ResponseCodeSF[],
              },
              new AbortController(),
            );

            // TODO: if sf sends 'pending app step' remove || keep ?
            // this just takes you to setup your account
            applicationResponseDTO.step_name = StepNames.YOUR_BUSINESS;
            applicationResponseDTO.substep_name = StepNames.DASHBOARD;
          }

          const application = getApplicationAPIFromDTO(
            applicationResponseDTO as ApplicationDTO,
            referenceNumber,
          );
          const documents = applicationResponseDTO.documents
            ? applicationResponseDTO.documents.map(getDocumentApiFromDocumentDTO)
            : undefined;

          return { application, documents };

        default: {
          throw new Error(
            `OTP revamp: error response from salesforce ${this.OTP_ENDPOINT_PATH} POST endpoint: response message: ${message}`,
          );
        }
      }
    });
  }

  async saveMerchantAgreement(
    merchant: MerchantAgreement,
    solutionType: SolutionType,
    abortController?: AbortController,
  ): Promise<void> {
    const document: DocumentAPI = {
      documentName: merchant.documentName,
      documentType: OnboardingDocumentTypeEnum.MERCHANT_AGREEMENT,
      documentBody: merchant.merchantAgreement,
      idNumber: merchant.Id,
    };

    return this.saveMerchantAgreementDocuments([document], solutionType, abortController);
  }

  /*
  * Save the merchant agreement documents to Salesforce.
  * documents: the documents to be saved
  * solutionType: the solution type of the application
  * abortController: the abort controller to cancel the request
  * return: void
  * @deprecated
  */

  async saveMerchantAgreementDocuments(
    documents: DocumentAPI[],
    solutionType: SolutionType,
    abortController?: AbortController,
  ): Promise<void> {
    const documentsInFrontend: DocumentDTO[] = documents.map(getDocumentDTOFromDocumentAPI);

    const documentsInSalesforce: DocumentDTO[] = await this.uploadDocumentsDTO(
      documentsInFrontend,
      solutionType,
      abortController,
    );

    const documentContentIdsToBeDeleted: string[] = documentsInSalesforce
      .filter((doc) => doc.content_document_id !== undefined)
      .map((doc) => doc.content_document_id as string);

    if (documentContentIdsToBeDeleted.length > 1) {
      // remove the first item from the array since it's the latest one.
      documentContentIdsToBeDeleted.shift();
      await this.deleteDocumentsDTO(documentContentIdsToBeDeleted, abortController);
    }
  }

  /* Limitation: this implementation can only be called after either
    getApplication or save Application has been called at least once.
   */
  async saveDocuments(
    documents: DocumentAPI[],
    solutionType: SolutionType,
    abortController?: AbortController,
    pendingAppFieldNames?: string[],
  ): Promise<void> {
    const documentsInFrontend: DocumentDTO[] = documents.map(getDocumentDTOFromDocumentAPI);

    const documentsInSalesforce: DocumentDTO[] = await this.uploadDocumentsDTO(
      documentsInFrontend,
      solutionType,
      abortController,
    );

    // Filter out documents that are not included as pending (i.e have an issue)
    const sfDocuments = documentsInSalesforce.filter((doc) =>
      pendingAppFieldNames?.includes(doc?.document_type || ''),
    );

    const documentContentIdsToBeDeleted: string[] = sfDocuments
      .filter((documentSalesforce) => {
        const isDocumentDeleted =
          documentSalesforce.document_type !== this.SELFIE_DOCUMENT_TYPE &&
          !documentsInFrontend.find((documentFrontend) =>
            this.isDocumentDTOsEqual(documentSalesforce, documentFrontend),
          );
        return (
          !!documentSalesforce.content_document_id &&
          isDocumentDeleted &&
          documentSalesforce.document_type !== VALIDATION_QUINTICA_FILE
        );
      })
      .map<string>((doc) => doc.content_document_id || '');

    if (documentContentIdsToBeDeleted.length > 0) {
      await this.deleteDocumentsDTO(documentContentIdsToBeDeleted, abortController);
    }
  }

  isDocumentDTOsEqual(documentA: DocumentDTO, documentB: DocumentDTO): boolean {
    return (
      documentA.document_name === documentB.document_name &&
      documentA.document_type === documentB.document_type &&
      ((!documentA.id_number && !documentB.id_number) ||
        documentA.id_number === documentB.id_number)
    );
  }

  getUploadDocSessionKey(solutionType: SolutionType): string {
    if (solutionType === SolutionType.thirdPartyOnboarding) {
      return this.SALESFORCE_APPLICATION_SESSION_3PO;
    }

    if (solutionType === SolutionType.buyInStoreFicaOnline) {
      return this.SALESFORCE_APPLICATION_SESSION_DEVICE_ACTIVATION;
    }

    return this.SALESFORCE_APPLICATION_SESSION;
  }

  async uploadDocumentsDTO(
    documents: DocumentDTO[],
    solutionType: SolutionType,
    abortController?: AbortController,
  ): Promise<DocumentDTO[]> {
    const session = await this.getSession(abortController);
    const sessionKey = this.getUploadDocSessionKey(solutionType);
    const applicationSession: SalesforceApplicationSession = this.cache.get(sessionKey);

    if (!applicationSession) {
      throw new Error(
        `Docs revamp: getApplication or saveApplication must be called before this function at least once: app session: ${applicationSession}`,
      );
    }

    const response = await request(
      this.DOCUMENTS_ENDPOINT_PATH,
      {
        fetchOptions: {
          method: 'POST',
          body: JSON.stringify({
            documents: documents,
            account_id: applicationSession.accountId,
            contact_id: applicationSession.contactId,
          }),
          headers: { Authorization: `Bearer ${session.accessToken}` },
          signal: abortController?.signal || null,
        },
      },
      session.instanceUrl,
      null,
      false,
    );
    const responseCode = response?.status;
    const responseBody = response?.result;
    if (
      (responseCode !== 200 || !responseBody?.ResponseData) &&
      !abortController?.signal?.aborted
    ) {
      throw new Error(
        `Docs revamp: error response from salesforce ${this.DOCUMENTS_ENDPOINT_PATH} POST endpoint: response code: ${responseCode}`,
      );
    }

    return (JSON.parse(responseBody.ResponseData).documents || []) as DocumentDTO[];
  }

  async deleteDocumentsDTO(contentIds: string[], abortController?: AbortController): Promise<void> {
    const session = await this.getSession(abortController);

    const response = await request(
      this.DOCUMENTS_ENDPOINT_PATH,
      {
        fetchOptions: {
          method: 'DELETE',
          body: JSON.stringify({
            documents: contentIds.map((id) => {
              return { content_document_id: id };
            }),
          }),
          headers: { Authorization: `Bearer ${session.accessToken}` },
          signal: abortController?.signal || null,
        },
      },
      session.instanceUrl,
      null,
      false,
    );

    const responseCode = response?.status;
    const responseBody = response?.result;
    const deleteDocumentDtos = JSON.parse(responseBody.Message) as DeleteDocumentDTO[];

    const isFailed = !!deleteDocumentDtos.find(
      (deletedDoc) => !deletedDoc.success || (deletedDoc.errors?.length || 0) > 0,
    );
    if ((responseCode !== 200 || isFailed) && !abortController?.signal?.aborted) {
      throw new Error(
        `Docs revamp: error response from salesforce ${this.DOCUMENTS_ENDPOINT_PATH} DELETE endpoint: response code: ${responseCode}`,
      );
    }
  }
}

export type SalesforceSession = {
  accessToken: string;
  instanceUrl: string;
};
